import { OidcAccessToken } from "@axa-fr/react-oidc/dist/ReactOidc";
import IChatMessage from "../../types/interfaces/IChatMessage";
import IModel from "../../types/interfaces/IModel";
import { DocumentChunk } from "../../types/interfaces/IConversationSettings";
import { ImpersonationRequest } from "./impersonation";
import { buildRequestHeaders } from "./requestHeaders";
import convertGatewayRequest from "./convertGatewayRequest";
import cleanMessages from "../comparisons-arrays/cleanMessages";
import trimConversationHistory from "../comparisons-arrays/trimConversationHistory";
import sendGatewayRequest from "./sendGatewayRequest";

interface FetchPromiseWithAbort extends Promise<Response> {
  abort: AbortController;
}


interface RAGRequestOptions {
  rag: string[];
  top_k: number;
  chunks?: DocumentChunk[];
  ragTemplate?: string;
  includeMetadata?: boolean;
  includeScores?: boolean;
  showRag: boolean;
}

/**
 * Sends a RAG search request to LLMGateway using the /rag/search endpoint
 * 
 * @param {IChatMessage[]} chatHistory - Array of chat messages.
 * @param {IModel} model - The model to be used for processing the chat messages.
 * @param {OidcAccessToken} accessToken - Access token for authorization.
 * @param {RAGRequestOptions} options - RAG-specific options
 * @param {ImpersonationRequest | null} [currentImpersonation] - Current impersonation context
 * 
 * @return {Promise<Response>} - A promise that resolves with the API response data.
 */
export async function sendRAGSearchRequest(
  chatHistory: IChatMessage[],
  model: IModel,
  accessToken: OidcAccessToken,
  options: RAGRequestOptions,
  currentImpersonation?: ImpersonationRequest | null,
): Promise<Response> {
  const host: string = process.env.REACT_APP_LLMGATEWAY_HOST ?? "";
  const trimmedHistory = trimConversationHistory(
    chatHistory,
    model,
    () => {},
  );

  // Prepare the RAG search request
  const gateway_api_request: any = {
    messages: cleanMessages(trimmedHistory),
    model: model.internal_name,
    rag: options.showRag ? options.rag : [],
    top_k: options.top_k || 1,
    include_chunks: true,
    reuse_chunks: options.chunks && options.chunks.length > 0,
    chunks: options.chunks ?? [],
    rag_template: options.ragTemplate || "default",
    include_metadata: options.includeMetadata !== false,
    include_scores: options.includeScores || false,
  };

  // When reusing chunks, we don't need to search for new documents
  if (gateway_api_request.reuse_chunks) {
    gateway_api_request.rag = []; // Clear RAG sources since we're using explicit chunk IDs
  }

  const headers: HeadersInit = buildRequestHeaders(accessToken, currentImpersonation);

  return fetch(`${host}/rag/search`, {
    method: "POST",
    body: JSON.stringify(convertGatewayRequest(gateway_api_request)),
    headers: headers,
  });
}

/**
 * Sends a RAG-enabled chat completion request to LLMGateway using the /rag/completions endpoint
 *
 * @param {IChatMessage[]} chatHistory - Array of chat messages.
 * @param {IModel} model - The model to be used for processing the chat messages.
 * @param {OidcAccessToken} accessToken - Access token for authorization.
 * @param {RAGRequestOptions} options - RAG-specific options
 * @param {Function} [popupCallback=() => {}] - Callback function for displaying popups.
 * @param {ImpersonationRequest | null} [currentImpersonation] - Current impersonation context
 *
 * @return {FetchPromiseWithAbort} - A promise that resolves with the API response data.
 */
export function sendRAGRequest(
  chatHistory: IChatMessage[],
  model: IModel,
  accessToken: OidcAccessToken,
  options: RAGRequestOptions,
  popupCallback: (s: string) => void = () => {},
  currentImpersonation?: ImpersonationRequest | null,
): FetchPromiseWithAbort {
  const host: string = process.env.REACT_APP_LLMGATEWAY_HOST ?? "";
  const abortController = new AbortController();
  const trimmedHistory = trimConversationHistory(
    chatHistory,
    model,
    popupCallback,
  );

  // Prepare the RAG request with enhanced parameters
  const gateway_api_request: any = {
    messages: cleanMessages(trimmedHistory),
    model: model.internal_name,
    rag: options.showRag ? options.rag : [],
    top_k: options.top_k || 1,
    stream: true,
    include_chunks: true,
    rag_template: options.ragTemplate || "default",
    include_metadata: options.includeMetadata !== false,
    include_scores: options.includeScores || false,
  };

  const headers: HeadersInit = buildRequestHeaders(accessToken, currentImpersonation);

  const { signal } = abortController;

  const fetchPromise = fetch(`${host}/rag/completions`, {
    method: "POST",
    body: JSON.stringify(convertGatewayRequest(gateway_api_request)),
    headers: headers,
    signal,
  }) as FetchPromiseWithAbort;

  fetchPromise.abort = abortController;
  
  if (trimmedHistory.length < 2) {
    fetchPromise.abort.abort(
      "Something went wrong: The message exceeded the context window. Please send a shorter message or consider selecting a model with a larger context window.",
    );
  }
  
  return fetchPromise;
}

/**
 * Enhanced gateway request that automatically routes between RAG and non-RAG endpoints
 * based on the presence of document chunks and RAG configuration.
 * 
 * @param {IChatMessage[]} chatHistory - Array of chat messages.
 * @param {IModel} model - The model to be used for processing the chat messages.
 * @param {OidcAccessToken} accessToken - Access token for authorization.
 * @param {string[]} rag - Array of RAG source identifiers.
 * @param {boolean} pii - Whether to enable PII detection.
 * @param {boolean} showRag - Whether to show RAG in the response.
 * @param {Function} [popupCallback=() => {}] - Callback function for displaying popups.
 * @param {number} [top_k=1] - Number of documents to retrieve.
 * @param {DocumentChunk[]} [ragChunks=[]] - Array of document chunks to use for completion.
 * @param {string} [ragTemplate="default"] - Template to use for RAG.
 * @param {boolean} [includeMetadata=true] - Whether to include metadata in the response.
 * @param {boolean} [includeScores=false] - Whether to include relevance scores.
 * @param {ImpersonationRequest | null} [currentImpersonation] - Current impersonation context
 * 
 * @return {FetchPromiseWithAbort} - A promise that resolves with the API response data.
 */
export function sendEnhancedGatewayRequest(
  chatHistory: IChatMessage[],
  model: IModel,
  accessToken: OidcAccessToken,
  rag: string[],
  pii: boolean,
  showRag: boolean,
  popupCallback: (s: string) => void = () => {},
  top_k: number = 1,
  ragChunks: DocumentChunk[] = [],
  ragTemplate: string = "default",
  includeMetadata: boolean = true,
  includeScores: boolean = false,
  currentImpersonation?: ImpersonationRequest | null,
): FetchPromiseWithAbort {
  // If RAG is enabled and we have document chunks, use document completion
  if (showRag && ragChunks && ragChunks.length > 0) {
    return sendDocumentCompletionRequest(
      chatHistory,
      model,
      accessToken,
      {
        rag,
        top_k,
        chunks: ragChunks,
        ragTemplate,
        includeMetadata,
        includeScores,
        showRag,
      },
      popupCallback,
      currentImpersonation,
    );
  }
  
  // Otherwise use the regular gateway request
  return sendGatewayRequest(
    chatHistory,
    model,
    accessToken,
    rag,
    pii,
    showRag,
    popupCallback,
    top_k,
    currentImpersonation
  );
}

/**
 * Sends a RAG document completion request to LLMGateway using the /rag/document-completion endpoint
 * 
 * @param {IChatMessage[]} chatHistory - Array of chat messages.
 * @param {IModel} model - The model to be used for processing the chat messages.
 * @param {OidcAccessToken} accessToken - Access token for authorization.
 * @param {RAGRequestOptions} options - RAG-specific options
 * @param {Function} [popupCallback=() => {}] - Callback function for displaying popups.
 * @param {ImpersonationRequest | null} [currentImpersonation] - Current impersonation context
 * 
 * @return {FetchPromiseWithAbort} - A promise that resolves with the API response data.
 */
export function sendDocumentCompletionRequest(
  chatHistory: IChatMessage[],
  model: IModel,
  accessToken: OidcAccessToken,
  options: RAGRequestOptions,
  popupCallback: (s: string) => void = () => {},
  currentImpersonation?: ImpersonationRequest | null,
): FetchPromiseWithAbort {
  const host: string = process.env.REACT_APP_LLMGATEWAY_HOST ?? "";
  const abortController = new AbortController();
  const trimmedHistory = trimConversationHistory(
    chatHistory,
    model,
    popupCallback,
  );

  // Ensure we have chunks
  if ((!options.chunks || options.chunks.length === 0) && (!options.chunks || options.chunks.length === 0)) {
    throw new Error("Document completion requires at least one document chunk");
  }

  // Prepare the document completion request
  const gateway_api_request: any = {
    messages: cleanMessages(trimmedHistory),
    model: model.internal_name,
    rag: options.showRag ? options.rag : [],
    top_k: options.top_k || 1,
    stream: true,
    include_chunks: false,
    chunks: options.chunks,
    rag_template: options.ragTemplate || "default",
    include_metadata: options.includeMetadata !== false,
    include_scores: options.includeScores || false,
  };

  const headers: HeadersInit = buildRequestHeaders(accessToken, currentImpersonation);

  const { signal } = abortController;

  const fetchPromise = fetch(`${host}/rag/document-completion`, {
    method: "POST",
    body: JSON.stringify(convertGatewayRequest(gateway_api_request)),
    headers: headers,
    signal,
  }).then(async (response) => {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    return response;
  }) as FetchPromiseWithAbort;

  fetchPromise.abort = abortController;
  
  if (trimmedHistory.length < 2) {
    fetchPromise.abort.abort(
      "Something went wrong: The message exceeded the context window. Please send a shorter message or consider selecting a model with a larger context window.",
    );
  }
  
  return fetchPromise;
} 